import { CSSInterpolation } from '@emotion/css/create-instance'
import { cx } from '@emotion/css'
import React, {
  type ComponentProps,
  type FocusEvent,
  type FocusEventHandler,
  type MouseEvent,
  type MouseEventHandler,
  type Ref,
} from 'react'

import type { ReactAnchorProps, ReactButtonProps } from '../../types'
import {
  CssVars,
  NeutralColorToken,
  SemanticColorToken,
  type ThemeColorToken,
} from '../themes'
import { useIsAchromaticTheme } from './AchromaticThemeOverride'
import { BaseButton } from './BaseButton'
import { AlertFilled16 } from './FoundationIcon/icons/AlertFilled16'
import { ArrowDown12 } from './FoundationIcon/icons/ArrowDown12'
import { ArrowLeft16 } from './FoundationIcon/icons/ArrowLeft16'
import { Checkmark12 } from './FoundationIcon/icons/Checkmark12'
import { Checkmark16 } from './FoundationIcon/icons/Checkmark16'
import { Copy12 } from './FoundationIcon/icons/Copy12'
import { FaceIdIos16 } from './FoundationIcon/icons/FaceIdIos16'
import { Lightning16 } from './FoundationIcon/icons/Lightning16'
import { LinkOut16 } from './FoundationIcon/icons/LinkOut16'
import { Lock12 } from './FoundationIcon/icons/Lock12'
import { Lock16 } from './FoundationIcon/icons/Lock16'
import { Order16 } from './FoundationIcon/icons/Order16'
import { PhoneConnected16 } from './FoundationIcon/icons/PhoneConnected16'
import { PlayInline16 } from './FoundationIcon/icons/PlayInline16'
import { Plus12 } from './FoundationIcon/icons/Plus12'
import { Plus16 } from './FoundationIcon/icons/Plus16'
import { Repeat16 } from './FoundationIcon/icons/Repeat16'
import { ThumbsDown16 } from './FoundationIcon/icons/ThumbsDown16'
import { ThumbsUp16 } from './FoundationIcon/icons/ThumbsUp16'
import { Trash16 } from './FoundationIcon/icons/Trash16'
import { InternalButtonContent } from './internal/InternalButtonContent'
import { PrimaryColorOverride } from './PrimaryColorOverride'
import { TextColorOverride } from './TextColorOverride'
import { useResolveToThemeColorToken } from './ThemeProvider'
import { TooltipTrigger } from './TooltipTrigger'
import { Type } from './Type'

type BaseProps = {
  label: string
  variant?: 'primary' | 'secondary' | false
  width?: 'full' | false
  state?: 'default' | 'loading' | 'disabled' | 'active' | false

  /**
   * @deprecated Use `backgroundColor` instead.
   */
  color?: SemanticColorToken | ThemeColorToken

  backgroundColor?: SemanticColorToken | ThemeColorToken
  foregroundColor?: SemanticColorToken | ThemeColorToken

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  overrideButtonStyles?: CSSInterpolation
  /**
   * Pass in `TootipTrigger` props in order to display a tooltip that will appear when you hover over
   * a button.
   */
  tooltipTriggerProps?: Omit<
    ComponentProps<typeof TooltipTrigger>,
    'renderTrigger'
  >
}

export type DefaultIcons =
  | 'Lock16'
  | 'Plus16'
  | 'Checkmark16'
  | 'LinkOut16'
  | 'ThumbsUp16'
  | 'ThumbsDown16'
  | 'PlayInline16'
  | 'AlertFilled16'
  | 'Repeat16'
  | 'Order16'
  | 'Trash16'
  | 'PhoneConnected16'
  | 'FaceIdIos16'
  | 'ArrowLeft16'
  | 'Lightning16'

export type CompactIcons =
  | 'ArrowDown12'
  | 'Lock12'
  | 'Plus12'
  | 'Checkmark12'
  | 'Copy12'

type SizeIntersection =
  | {
      size: 'compact'
      // NOTE(alan.duan): We intentionally only import & support icons one by one now, because supporting
      // all FoundationIcons will significantly increase bundle size as we import FoundationIcon.
      // TODO(jare): Use Icon12 type for compact and Icon16 type for default once the above is no longer true.
      /**
       * An icon to render inside of the button. Compact buttons only support size 12 icons, while default buttons
       * only support size 16 icons.
       */
      icon?: CompactIcons
    }
  | {
      size?: 'default' | undefined
      icon?: DefaultIcons
    }

type OmittedElementPropKeys =
  | keyof BaseProps
  | 'ref'
  | 'disabled'
  | 'className'
  | 'style'
  | 'css'
  | 'type'

type AnchorProps = {
  type: 'anchor'
  innerRef?: Ref<HTMLAnchorElement>
} & Omit<ReactAnchorProps, OmittedElementPropKeys>

type ButtonProps = {
  type: 'button' | 'submit' | 'reset'
  innerRef?: Ref<HTMLButtonElement>
} & Omit<ReactButtonProps, OmittedElementPropKeys>

export type Props = BaseProps & (AnchorProps | ButtonProps) & SizeIntersection

export function Button({
  label,
  variant: variantProp = 'primary',
  width,
  state: stateProp = 'default',
  overrideRootStyles,
  overrideButtonStyles,
  color = SemanticColorToken.Primary,
  backgroundColor,
  foregroundColor,
  size,
  icon,
  tooltipTriggerProps,
  ...props
}: Props) {
  const state = stateProp || 'default'
  const variant = variantProp || 'primary'

  const isClickable = state !== 'disabled' && state !== 'loading'
  const isAchromaticTheme = useIsAchromaticTheme()

  const css: CSSInterpolation = [
    {
      // NOTE(yan): when the border-radius value is larger than available
      //  half width or length, css will make the largest possible quarter
      //  circle. So just set it to the height so that it always makes a
      //  quarter circle for the given button height without needing `calc`
      //  see https://www.w3.org/Style/CSS/Tracker/issues/29
      borderRadius: CssVars.size.buttonHeight,
      // NOTE(jare): Use color so we can automatically toggle the color of the button
      // between the enabled and disabled states
      color:
        state === 'disabled'
          ? CssVars.neutral.fg3
          : CssVars.semantic.primary.base,
      height: CssVars.size.buttonHeight,
      maxWidth: 420,
      minWidth: 120,
      paddingLeft: `calc(4 * ${CssVars.size.gutterWidth})`,
      paddingRight: `calc(4 * ${CssVars.size.gutterWidth})`,
      whiteSpace: 'nowrap',
    },
    size === 'compact' && {
      height: CssVars.size.cellHeightSm,
      minWidth: 96,
      paddingLeft: `calc(2 * ${CssVars.size.gutterWidth})`,
      paddingRight: `calc(2 * ${CssVars.size.gutterWidth})`,
    },
    // TODO(auster.chen): Change these three `backgroundColor` properties back to `color`
    // when this Safari `currentColor` bug is fixed: https://bugs.webkit.org/show_bug.cgi?id=223534.
    variant === 'primary' && [
      {
        backgroundColor: 'currentColor',
      },
      // NOTE(trevor.healy): Order of the pseudo classes matter, and key sorting will try to put :active before :hover
      isClickable && {
        '&:hover': {
          backgroundColor: CssVars.semantic.primary.hover,
        },
      },
      isClickable && {
        '&:active': {
          backgroundColor: CssVars.semantic.primary.pressed,
        },
      },
      state === 'active' && {
        backgroundColor: CssVars.semantic.primary.pressed,
      },
    ],
    variant === 'secondary' && [
      {
        borderColor: 'currentColor',
        borderStyle: 'solid',
        borderWidth: 1,
      },
      isAchromaticTheme && {
        // HACK(yan):
        //  achromatic secondary button has anti-aliasing issue with the 1px
        //    fine border on non-retina screens. especially around the
        //    rounded edges where artifacts are most noticeable. this is due
        //    to harsh contrast of jet / nova.
        //  instead of drawing the 1px border, use the box-shadow to draw
        //    an effective 1.2px border overlayed on the button. this helps
        //    alleviates most artifacts.
        //  only target this hack for non-retina displays, and safari doesn't
        //    support max-resolution so use the webkit alternative.
        //    see: https://bugs.webkit.org/show_bug.cgi?id=78087
        '@media only screen and (max-resolution:1dppx), only screen and (-webkit-min-device-pixel-ratio: 1)':
          {
            '::before': {
              // NOTE(yan): same border radius trick as noted above but set to
              //   an impossibly large value here so that it'll always be
              //   rounded no matter how much the button is scaled up
              borderRadius: 999999,
              boxShadow: 'inset 0 0 0 6px currentColor',
              content: "''",
              display: 'block',
              height: '500%',
              left: 0,
              position: 'absolute',
              top: 0,
              transform: 'scale(0.2)',
              transformOrigin: '0 0',
              width: '500%',
            },
            // add in left/right borders to maintain the same width
            borderBottom: 0,
            borderColor: 'transparent',
            borderLeftWidth: 1,
            borderRightWidth: 1,
            borderTop: 0,
            position: 'relative',
          },
      },
      isClickable && {
        '&:hover': {
          backgroundColor: CssVars.semantic.primary.lightest,
        },
        // NOTE(trevor.healy): Order of the pseudo classes matter, and key sorting will try to put :active before :hover
        /* eslint-disable-next-line sort-keys-fix/sort-keys-fix */
        '&:active': {
          backgroundColor: CssVars.semantic.primary.light,
        },
      },
      state === 'active' && {
        backgroundColor: CssVars.semantic.primary.light,
      },
    ],
    width === 'full' && {
      width: '100%',
    },
  ]
  const textColor = useTextColor(variant, state, foregroundColor)

  return (
    <PrimaryColorOverride color={backgroundColor ?? color}>
      {({ rootClassName: primaryColorClassName }) => (
        <TextColorOverride color={textColor}>
          {({ rootClassName: textColorClassName }) => {
            const defaultButtonProps = {
              'aria-label':
                // NOTE(yan): when the button is loading, a spinner will show instead of the
                //   provided label. however, for a11y we need to always provide a label. so
                //   when loading label this component with the label text unless there is
                //   already an external label provided.
                state === 'loading' &&
                !props['aria-label'] &&
                !props['aria-labelledby']
                  ? label
                  : // return undefine here even if may aria-label exist because the props
                    // spread below will just override this prop should it exist
                    undefined,
              overrideRootStyles: [css, overrideButtonStyles],
              // BaseButton doesn't support an 'active' state, but we want the same behavior
              // as 'default' for it.
              state: state === 'active' ? 'default' : state,
              ...props,
            }

            const buttonContent = (
              <InternalButtonContent state={state}>
                {!!icon && <ButtonIcon type={icon} />}
                <Type.Text isBold size="S">
                  {label}
                </Type.Text>
              </InternalButtonContent>
            )

            return (
              <div
                className={cx(primaryColorClassName, textColorClassName)}
                css={[
                  // NOTE(eric.lovett): If the button is full-width, make sure that it's centered
                  // if our parent is a container that's wider than the button's maximum width.
                  width === 'full' && { textAlign: 'center', width: '100%' },
                  overrideRootStyles,
                ]}
              >
                {tooltipTriggerProps ? (
                  <TooltipTrigger
                    {...tooltipTriggerProps}
                    renderTrigger={({
                      'aria-describedby': ariaDescribedBy,
                      closeTooltip,
                      openTooltip,
                    }) => (
                      <BaseButton
                        {...defaultButtonProps}
                        aria-describedby={ariaDescribedBy}
                        // Note(@auster.chen): The following handlers are a little verbose due to TS issues
                        onBlur={(
                          event:
                            | FocusEvent<HTMLButtonElement>
                            | FocusEvent<HTMLAnchorElement>
                        ) => {
                          closeTooltip()

                          if (defaultButtonProps.type === 'button') {
                            ;(
                              defaultButtonProps?.onBlur as FocusEventHandler<HTMLButtonElement>
                            )?.(event as FocusEvent<HTMLButtonElement>)
                          } else {
                            ;(
                              defaultButtonProps?.onBlur as FocusEventHandler<HTMLAnchorElement>
                            )?.(event as FocusEvent<HTMLAnchorElement>)
                          }
                        }}
                        onFocus={(
                          event:
                            | FocusEvent<HTMLButtonElement>
                            | FocusEvent<HTMLAnchorElement>
                        ) => {
                          openTooltip()

                          if (defaultButtonProps.type === 'button') {
                            ;(
                              defaultButtonProps?.onFocus as FocusEventHandler<HTMLButtonElement>
                            )?.(event as FocusEvent<HTMLButtonElement>)
                          } else {
                            ;(
                              defaultButtonProps?.onFocus as FocusEventHandler<HTMLAnchorElement>
                            )?.(event as FocusEvent<HTMLAnchorElement>)
                          }
                        }}
                        onMouseEnter={(
                          event:
                            | MouseEvent<HTMLButtonElement>
                            | MouseEvent<HTMLAnchorElement>
                        ) => {
                          openTooltip()

                          if (defaultButtonProps.type === 'button') {
                            ;(
                              defaultButtonProps?.onMouseEnter as MouseEventHandler<HTMLButtonElement>
                            )?.(event as MouseEvent<HTMLButtonElement>)
                          } else {
                            ;(
                              defaultButtonProps?.onMouseEnter as MouseEventHandler<HTMLAnchorElement>
                            )?.(event as MouseEvent<HTMLAnchorElement>)
                          }
                        }}
                        onMouseLeave={(
                          event:
                            | MouseEvent<HTMLButtonElement>
                            | MouseEvent<HTMLAnchorElement>
                        ) => {
                          closeTooltip()

                          if (defaultButtonProps.type === 'button') {
                            ;(
                              defaultButtonProps?.onMouseLeave as MouseEventHandler<HTMLButtonElement>
                            )?.(event as MouseEvent<HTMLButtonElement>)
                          } else {
                            ;(
                              defaultButtonProps?.onMouseLeave as MouseEventHandler<HTMLAnchorElement>
                            )?.(event as MouseEvent<HTMLAnchorElement>)
                          }
                        }}
                      >
                        {buttonContent}
                      </BaseButton>
                    )}
                  />
                ) : (
                  <BaseButton {...defaultButtonProps}>
                    {buttonContent}
                  </BaseButton>
                )}
              </div>
            )
          }}
        </TextColorOverride>
      )}
    </PrimaryColorOverride>
  )
}

function useTextColor(
  variant: Props['variant'],
  state: Props['state'],
  foregroundColor: Props['foregroundColor']
): SemanticColorToken | ThemeColorToken {
  // If the user sets `foregroundColor` to `semantic.primary`, they're expecting the result
  // to be Prime/Joule. But because we override the primary color in order to get the hover
  // colors, we need to resolve a semantic color to the underlying neutral or extended palette
  // color here. See DS-2272.
  const resolvedTextColor = useResolveToThemeColorToken(foregroundColor)

  if (variant === 'secondary') {
    if (state === 'disabled') {
      // When the button is disabled, the text is always FG3 even if overrides were specified.
      return NeutralColorToken.Fg3
    }
    return foregroundColor || SemanticColorToken.Primary
  }
  return resolvedTextColor || NeutralColorToken.Bg1
}

export const supportedIcons = {
  AlertFilled16,
  ArrowDown12,
  ArrowLeft16,
  Checkmark12,
  Checkmark16,
  Copy12,
  FaceIdIos16,
  Lightning16,
  LinkOut16,
  Lock12,
  Lock16,
  Order16,
  PhoneConnected16,
  PlayInline16,
  Plus12,
  Plus16,
  Repeat16,
  ThumbsDown16,
  ThumbsUp16,
  Trash16,
}

function ButtonIcon({ type }: { type: Exclude<Props['icon'], undefined> }) {
  const IconComponent = supportedIcons[type]
  return (
    <span
      // TODO(yan): hide this until we figure out the right alt text for icons
      //   https://robinhood.atlassian.net/browse/DS-2169
      aria-hidden
      css={{
        // NOTE(jare): Since we're rendering the icon directly instead of using
        // FoundationIcon, we have to add display: inline-flex to make sure the
        // icon is rendered without any extra space around it
        display: 'inline-flex',
        marginRight: CssVars.size.gutterWidthSm,
      }}
    >
      <IconComponent />
    </span>
  )
}
